import React, { useEffect, useRef, useState } from "react";
import { REIButton, REIDropDown, REIInputText, REIToastContainer } from "../../../components/ui";
import { MEMBER_KEY, Messages } from "../../../utils/constants";
import { httpPost } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { Toast } from "primereact/toast";

interface ContactDetails {
  name: string;
  role: string;
  company: string;
  phone: string;
  mobile: string;
  email: string;
  address: string;
  website: string;
  notes: string;
}

const AddContactDetails: React.FC<{
  propertyTrackerId: number;
  closeDialogue: () => void;
  editMode: boolean;
  contact: IContactDetailInfo | null;
  properties: any[] | undefined | null;
}> = ({ propertyTrackerId, closeDialogue, editMode, contact, properties }) => {
  const memberId = localStorage.getItem(MEMBER_KEY);
  const toastRef = useRef<Toast>(null);
  const [contactDetails, setContactDetails] = useState<ContactDetails>({
    name: "",
    role: "",
    company: "",
    phone: "",
    mobile: "",
    email: "",
    address: "",
    website: "",
    notes: "",
  });
  const [selectedProperty, setSelectedProperty] = useState<number>(propertyTrackerId > 0 ? propertyTrackerId : 0);

  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (editMode && contact) {
      setContactDetails({
        name: contact.Name,
        role: contact.Role,
        company: contact.Company,
        phone: contact.Phone,
        mobile: contact.Mobile,
        email: contact.Email,
        address: contact.Address,
        website: contact.Website,
        notes: contact.Note ?? "",
      });
      if (contact.PropertyTrackerId == "") {
        setSelectedProperty(0);
      } else {
        setSelectedProperty(parseInt(contact.PropertyTrackerId) || 0);
      }
    }
  }, [editMode, contact]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { name?: string } = {};
    if (!contactDetails.name.trim()) {
      newErrors.name = "Name is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (editMode) {
      await updateContactDetails();
    } else {
      await addContactDetails();
    }
  };

  const addContactDetails = async () => {
    const body = {
      Name: contactDetails.name,
      Role: contactDetails.role,
      Company: contactDetails.company,
      Phone: contactDetails.phone,
      Mobile: contactDetails.mobile,
      Email: contactDetails.email,
      Address: contactDetails.address,
      Website: contactDetails.website,
      Note: contactDetails.notes,
      MemberId: memberId,
      PropertyTrackerId: properties ? (selectedProperty === 0 ? "" : selectedProperty) : propertyTrackerId,
    };
    await httpPost(`${ApiEndPoints.tracker}MemberContacts/Add/`, body)
      .then((result) => {
        if (result.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Contact Added Successfully",
            life: 2000,
          });
          setTimeout(() => {
            closeDialogue();
          }, 1000);
        }
      })
      .catch(() => {
        toastRef.current?.show({
          severity: "success",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
      });
  };

  const updateContactDetails = async () => {
    const body = {
      Name: contactDetails.name,
      Role: contactDetails.role,
      Company: contactDetails.company,
      Phone: contactDetails.phone,
      Mobile: contactDetails.mobile,
      Email: contactDetails.email,
      Address: contactDetails.address,
      Website: contactDetails.website,
      Note: contactDetails.notes,
      MemberId: memberId,
      PropertyTrackerId: selectedProperty == 0 ? "" : selectedProperty,
      MemberContactId: contact?.MemberContactId,
    };
    await httpPost(`${ApiEndPoints.tracker}MemberContacts/Update/`, body)
      .then((result) => {
        if (result.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Contact Updated Successfully",
            life: 2000,
          });
          setTimeout(() => {
            closeDialogue();
          }, 1000);
        }
      })
      .catch(() => {
        toastRef.current?.show({
          severity: "success",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
      });
  };

  const handleCancel = () => {
    closeDialogue();
  };

  return (
    <div className="w-full mx-auto p-4">
      <REIToastContainer toastReference={toastRef}></REIToastContainer>
      {properties && (
        <div className="flex flex-col">
          <div className="flex flex-row flex-wrap mb-2">
            <label className="self-center mr-3" htmlFor="property">
              Property address:
            </label>
            <REIDropDown
              id="mpacon-drop-property"
              key={`property-${selectedProperty}`}
              value={selectedProperty}
              data={properties || []}
              label="label"
              onSelectionChange={(e) => {
                setSelectedProperty(e.value);
              }}
              placeholder="Select Property"
              className="w-min"
            />
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block mb-1">
              Name<span className="text-red-600">*</span>
            </label>
            <REIInputText
              id="mpacon-txt-name"
              type="text"
              name="name"
              value={contactDetails.name}
              onChange={handleInputChange}
              className={`w-full p-inputtext-sm ${errors.name ? "border-red-600" : ""}`}
            />
            {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block mb-1">Role</label>
            <REIInputText
              id="mpacon-txt-role"
              type="text"
              name="role"
              value={contactDetails.role}
              onChange={handleInputChange}
              className="w-full p-inputtext-sm"
            />
          </div>

          <div>
            <label className="block mb-1">Company</label>
            <REIInputText
              id="mpacon-txt-company"
              type="text"
              name="company"
              value={contactDetails.company}
              onChange={handleInputChange}
              className="w-full p-inputtext-sm"
            />
          </div>

          <div>
            <label className="block mb-1">Phone</label>
            <REIInputText
              id="mpacon-txt-phone"
              type="tel"
              name="phone"
              value={contactDetails.phone}
              onChange={handleInputChange}
              className="w-full p-inputtext-sm"
            />
          </div>

          <div>
            <label className="block mb-1">Mobile</label>
            <REIInputText
              id="mpacon-txt-mobile"
              type="tel"
              name="mobile"
              value={contactDetails.mobile}
              onChange={handleInputChange}
              className="w-full p-inputtext-sm"
            />
          </div>

          <div>
            <label className="block mb-1">Email</label>
            <REIInputText
              id="mpacon-txt-email"
              type="email"
              name="email"
              value={contactDetails.email}
              onChange={handleInputChange}
              className="w-full p-inputtext-sm"
            />
          </div>
        </div>

        <div>
          <label className="block mb-1">Contact's address</label>
          <REIInputText
            id="mpacon-txt-address"
            type="text"
            name="address"
            value={contactDetails.address}
            onChange={handleInputChange}
            className="w-full p-inputtext-sm"
          />
        </div>

        <div>
          <label className="block mb-1">Website</label>
          <REIInputText
            id="mpacon-txt-website"
            type="text"
            name="website"
            value={contactDetails.website}
            onChange={handleInputChange}
            className="w-full p-inputtext-sm"
          />
        </div>

        {!editMode && (
          <div>
            <label className="block mb-1">Notes</label>
            <textarea
              id="mpacon-txt-notes"
              data-testid="mpacon-txt-notes"
              name="notes"
              value={contactDetails.notes}
              onChange={handleInputChange}
              className="w-full border rounded p-2 h-32"
            />
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <REIButton
            type="button"
            id="mpacon-btn-cancel"
            label="Cancel"
            onClick={() => handleCancel()}
            severity="secondary"
          />
          <REIButton type="submit" id="mpacon-btn-save" label="Save" onClick={() => {}} />
        </div>
      </form>
    </div>
  );
};

export default AddContactDetails;
